import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Alert,
  Tooltip,
  Checkbox,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  GetApp as GetAppIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { Candidate, EmailComposerData } from '../../types';
import { useNavigate } from 'react-router-dom';
import airtableService from '../../services/airtable';
import emailService from '../../services/emailService';
import EmailComposer from '../../components/EmailComposer';

const CandidatesList: React.FC = () => {
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAnchor, setFilterAnchor] = useState<null | HTMLElement>(null);
  const [sortAnchor, setSortAnchor] = useState<null | HTMLElement>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('firstName');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCandidates, setSelectedCandidates] = useState<Set<string>>(new Set());
  const [emailComposerOpen, setEmailComposerOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCandidates();
  }, []);

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await airtableService.getCandidates();

      if (response.success) {
        setCandidates(response.data);
      } else {
        setError(response.error || 'Failed to fetch candidates');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch candidates');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'New': return 'info';
      case 'Screening': return 'warning';
      case 'In Process': return 'primary';
      case 'Hired': return 'success';
      case 'Rejected': return 'error';
      default: return 'default';
    }
  };

  const filteredAndSortedCandidates = candidates
    .filter(candidate => {
      const matchesSearch = `${candidate.firstName} ${candidate.lastName} ${candidate.email} ${candidate.currentJobTitle}`.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = !statusFilter || candidate.status === statusFilter;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortBy) {
        case 'firstName':
          aValue = a.firstName?.toLowerCase() || '';
          bValue = b.firstName?.toLowerCase() || '';
          break;
        case 'lastName':
          aValue = a.lastName?.toLowerCase() || '';
          bValue = b.lastName?.toLowerCase() || '';
          break;
        case 'location':
          aValue = a.currentLocation?.toLowerCase() || '';
          bValue = b.currentLocation?.toLowerCase() || '';
          break;
        case 'dateActivated':
          aValue = a.dateActivated ? new Date(a.dateActivated).getTime() : 0;
          bValue = b.dateActivated ? new Date(b.dateActivated).getTime() : 0;
          break;
        default:
          aValue = a.firstName?.toLowerCase() || '';
          bValue = b.firstName?.toLowerCase() || '';
      }

      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchor(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchor(null);
  };

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
    setFilterAnchor(null);
  };

  const handleSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setSortAnchor(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchor(null);
  };

  const handleSortChange = (field: string, order: 'asc' | 'desc') => {
    setSortBy(field);
    setSortOrder(order);
    setSortAnchor(null);
  };

  const handleCandidateSelect = (candidateId: string, checked: boolean) => {
    const newSelection = new Set(selectedCandidates);
    if (checked) {
      newSelection.add(candidateId);
    } else {
      newSelection.delete(candidateId);
    }
    setSelectedCandidates(newSelection);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(filteredAndSortedCandidates.map(c => c.id));
      setSelectedCandidates(allIds);
    } else {
      setSelectedCandidates(new Set());
    }
  };

  const handleBulkEmail = () => {
    if (selectedCandidates.size > 0) {
      setEmailComposerOpen(true);
    }
  };

  const handleSendEmail = async (emailData: EmailComposerData) => {
    try {
      const queueResponse = await emailService.addToQueue(emailData, 'current-user-id');
      if (queueResponse.success) {
        console.log('✅ Bulk email queued successfully');
        setSelectedCandidates(new Set()); // Clear selection after sending
      } else {
        console.error('❌ Failed to queue email:', queueResponse.error);
        throw new Error(queueResponse.error);
      }
    } catch (error) {
      console.error('Error sending bulk email:', error);
      throw error;
    }
  };

  const getSelectedCandidatesData = (): Candidate[] => {
    return candidates.filter(candidate => selectedCandidates.has(candidate.id));
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Typography variant="h6">
          Unable to load candidates. Please check your Airtable configuration.
        </Typography>
        <Button variant="outlined" onClick={fetchCandidates} sx={{ mt: 2 }}>
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Candidates ({filteredAndSortedCandidates.length})
          {selectedCandidates.size > 0 && (
            <Chip
              label={`${selectedCandidates.size} selected`}
              color="primary"
              sx={{ ml: 2 }}
            />
          )}
        </Typography>
        {selectedCandidates.size > 0 && (
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={handleBulkEmail}
          >
            Send Email to {selectedCandidates.size} Candidates
          </Button>
        )}
      </Box>

      {/* Search, Filter, and Sort */}
      <Box display="flex" gap={2} mb={3} alignItems="center">
        <Tooltip title="Select All">
          <Checkbox
            checked={selectedCandidates.size === filteredAndSortedCandidates.length && filteredAndSortedCandidates.length > 0}
            indeterminate={selectedCandidates.size > 0 && selectedCandidates.size < filteredAndSortedCandidates.length}
            onChange={(e) => handleSelectAll(e.target.checked)}
          />
        </Tooltip>
        <TextField
          placeholder="Search candidates..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1 }}
        />
        <IconButton onClick={handleFilterClick}>
          <FilterIcon />
        </IconButton>
        <IconButton onClick={handleSortClick}>
          <SortIcon />
        </IconButton>
        <Menu
          anchorEl={filterAnchor}
          open={Boolean(filterAnchor)}
          onClose={handleFilterClose}
        >
          <MenuItem onClick={() => handleStatusFilter('')}>All Status</MenuItem>
          <MenuItem onClick={() => handleStatusFilter('New')}>New</MenuItem>
          <MenuItem onClick={() => handleStatusFilter('Screening')}>Screening</MenuItem>
          <MenuItem onClick={() => handleStatusFilter('In Process')}>In Process</MenuItem>
          <MenuItem onClick={() => handleStatusFilter('Hired')}>Hired</MenuItem>
          <MenuItem onClick={() => handleStatusFilter('Rejected')}>Rejected</MenuItem>
        </Menu>
        <Menu
          anchorEl={sortAnchor}
          open={Boolean(sortAnchor)}
          onClose={handleSortClose}
        >
          <MenuItem onClick={() => handleSortChange('firstName', 'asc')}>First Name (A-Z)</MenuItem>
          <MenuItem onClick={() => handleSortChange('firstName', 'desc')}>First Name (Z-A)</MenuItem>
          <MenuItem onClick={() => handleSortChange('lastName', 'asc')}>Last Name (A-Z)</MenuItem>
          <MenuItem onClick={() => handleSortChange('lastName', 'desc')}>Last Name (Z-A)</MenuItem>
          <MenuItem onClick={() => handleSortChange('location', 'asc')}>Location (A-Z)</MenuItem>
          <MenuItem onClick={() => handleSortChange('location', 'desc')}>Location (Z-A)</MenuItem>
          <MenuItem onClick={() => handleSortChange('dateActivated', 'asc')}>Date Activated (Oldest First)</MenuItem>
          <MenuItem onClick={() => handleSortChange('dateActivated', 'desc')}>Date Activated (Newest First)</MenuItem>
        </Menu>

        {/* Sort Indicator */}
        {(sortBy !== 'firstName' || sortOrder !== 'asc') && (
          <Chip
            label={`Sorted by: ${
              sortBy === 'firstName' ? 'First Name' :
              sortBy === 'lastName' ? 'Last Name' :
              sortBy === 'location' ? 'Location' :
              sortBy === 'dateActivated' ? 'Date Activated' :
              'First Name'
            } (${sortOrder === 'asc' ? 'A-Z' : 'Z-A'})`}
            variant="outlined"
            size="small"
            onDelete={() => {
              setSortBy('firstName');
              setSortOrder('asc');
            }}
            sx={{ ml: 1 }}
          />
        )}
      </Box>

      {/* Candidates Grid */}
      <Grid container spacing={3}>
        {filteredAndSortedCandidates.map((candidate) => (
          <Grid item xs={12} md={6} lg={4} key={candidate.id} sx={{ display: 'flex' }}>
            <Card
              elevation={2}
              sx={{
                cursor: 'pointer',
                '&:hover': { elevation: 4 },
                transition: 'box-shadow 0.2s',
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative'
              }}
              onClick={() => navigate(`/candidates/${candidate.id}`)}
            >
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Checkbox
                    checked={selectedCandidates.has(candidate.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleCandidateSelect(candidate.id, e.target.checked);
                    }}
                    sx={{ mr: 1 }}
                  />
                  <Avatar
                    sx={{ bgcolor: 'primary.main', mr: 2, width: 56, height: 56 }}
                    src={candidate.candidateImage?.[0]?.thumbnails?.small?.url || candidate.candidateImage?.[0]?.url}
                  >
                    {!candidate.candidateImage?.length && <PersonIcon />}
                  </Avatar>
                  <Box flexGrow={1}>
                    <Typography variant="h6">
                      {candidate.firstName} {candidate.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {candidate.currentJobTitle} at {candidate.currentCompany}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      label={candidate.status}
                      color={getStatusColor(candidate.status) as any}
                      size="small"
                    />
                    {candidate.resumeAttachment?.[0] && (
                      <Tooltip title="Download Resume">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(candidate.resumeAttachment![0].url, '_blank');
                          }}
                        >
                          <GetAppIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>

                <Box display="flex" flexDirection="column" gap={1} sx={{ flexGrow: 1 }}>
                  <Box display="flex" alignItems="center">
                    <EmailIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {candidate.email}
                    </Typography>
                  </Box>
                  {candidate.phone && (
                    <Box display="flex" alignItems="center">
                      <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {candidate.phone}
                      </Typography>
                    </Box>
                  )}
                  <Box display="flex" alignItems="center">
                    <LocationIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {candidate.currentLocation}
                      {candidate.willingToRelocate && ' • Open to relocation'}
                    </Typography>
                  </Box>
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                  <Typography variant="body2" color="text.secondary">
                    {candidate.yearsOfExperience} years exp.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Activated {(() => {
                      try {
                        const date = candidate.dateActivated ? new Date(candidate.dateActivated) : null;
                        return date && !isNaN(date.getTime()) ? date.toLocaleDateString() : 'Not activated';
                      } catch {
                        return 'Not activated';
                      }
                    })()}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {filteredAndSortedCandidates.length === 0 && (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="text.secondary">
            No candidates found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search criteria or add a new candidate
          </Typography>
        </Box>
      )}

      {/* Email Composer for Bulk Emails */}
      <EmailComposer
        open={emailComposerOpen}
        onClose={() => setEmailComposerOpen(false)}
        recipients={getSelectedCandidatesData()}
        onSend={handleSendEmail}
      />
    </Box>
  );
};

export default CandidatesList;